import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Envelope, GoogleLogo, ArrowRight, UserCircle, Buildings, CheckCircle, LockKey, ChatCircleDots, Sparkle, MicrosoftOutlookLogo, AppleLogo } from "@phosphor-icons/react";
import axios from "axios";
import LanguagePicker from "@/components/LanguagePicker";
import InstallAppButton from "@/components/InstallAppButton";
import { useT } from "@/lib/i18n";
import { useBranding } from "@/lib/branding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

// User-friendly labels for /api/auth/providers 503 responses and ?oauth_error= codes.
const OAUTH_ERROR_MESSAGES = {
  access_denied: "You cancelled the login — no problem, feel free to try a different method.",
  invalid_state: "Your login session expired. Please try signing in again.",
  missing_code: "Login provider didn't return an authorization code. Please retry.",
  no_id_token: "Login provider didn't return an ID token. Please retry.",
  invalid_id_token: "We couldn't verify your identity token. Please try again.",
  token_exchange_failed: "Token exchange with the provider failed. Try again in a moment.",
  legacy_session_retired: "That older login flow has been retired. Please start sign-in again.",
  missing_email: "Your provider didn't share an email address. Enable email sharing and retry.",
  unknown_provider: "That login provider is not supported.",
  link_failed_409: "This external account is already linked to a different user. Sign in with that email.",
  link_failed: "We couldn't link your external account. Please contact support.",
};

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useT();
  const branding = useBranding();
  const [mode, setMode] = useState("choose"); // choose | email | customer
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(false); // extend session to 30 days
  const [providers, setProviders] = useState({ google: false, microsoft: false, apple: false });
  // Customer flow state
  const [custStep, setCustStep] = useState("email"); // email | setPassword | verify | login
  const [custInfo, setCustInfo] = useState(null); // { email, name, company_name, has_password, verified }
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [token, setToken] = useState("");
  const [notice, setNotice] = useState("");

  // On mount: fetch provider availability + surface OAuth callback errors.
  useEffect(() => {
    ax.get(`${API}/auth/providers`)
      .then((r) => {
        const map = {};
        for (const p of r.data?.providers || []) map[p.name] = !!p.configured;
        setProviders(map);
      })
      .catch(() => {});
    const err = searchParams.get("oauth_error");
    if (err) {
      setError(
        OAUTH_ERROR_MESSAGES[err] ||
          `Login failed: ${searchParams.get("oauth_detail") || err}`
      );
    }
  }, [searchParams]);

  const handleGoogleLogin = () => {
    handleOAuthLogin("google");
  };

  // Microsoft + Apple share one redirect helper — the backend knows which
  // provider we're hitting from the URL path and handles the rest.
  const handleOAuthLogin = (provider) => {
    const qs = new URLSearchParams({
      remember: remember ? "true" : "false",
      return_to: "/",
    });
    window.location.href = `${API}/auth/${provider}/login?${qs.toString()}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email"); return; }
    setLoading(true); setError(""); setNotice("");
    try {
      // Check if this is a customer contact first — if so, swap to customer flow.
      const check = await ax.post(`${API}/customer-auth/check`, { email: email.trim() });
      if (check.data?.is_customer_contact) {
        setCustInfo(check.data);
        setMode("customer");
        setCustStep(check.data.verified && check.data.has_password ? "login" : "setPassword");
      } else {
        // Non-customer email-only fallback. Staff/admin users must use OAuth.
        const res = await ax.post(`${API}/auth/login`, { email: email.trim(), remember });
        onLogin(res.data);
      }
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Could not sign in. Please try again.");
    } finally { setLoading(false); }
  };

  const handleSendVerification = async (e) => {
    e.preventDefault();
    if (!password || password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== password2) { setError("Passwords do not match"); return; }
    setLoading(true); setError("");
    try {
      const r = await ax.post(`${API}/customer-auth/request-verification`,
        { email: custInfo.email, password });
      setNotice(r.data?.message || "Verification code sent.");
      setCustStep("verify");
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Failed to send verification");
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!token.trim()) { setError("Enter the code from your email"); return; }
    setLoading(true); setError("");
    try {
      await ax.post(`${API}/customer-auth/verify`, { email: custInfo.email, token: token.trim() });
      setNotice("Account verified. Please sign in.");
      setCustStep("login");
      setPassword(""); setPassword2(""); setToken("");
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const handleCustomerLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const r = await ax.post(`${API}/customer-auth/login`, { email: custInfo.email, password, remember });
      onLogin({ ...r.data, is_admin: false, role: "customer" });
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Invalid password");
    } finally { setLoading(false); }
  };

  const handleGuest = () => {
    // Stable pseudo-email so the guest's AI chat sessions persist across reloads
    let guestEmail = localStorage.getItem("usair_guest_email");
    if (!guestEmail) {
      const rnd = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)).replace(/-/g, "").slice(0, 12);
      guestEmail = `guest-${rnd}@local.guest`;
      localStorage.setItem("usair_guest_email", guestEmail);
    }
    onLogin({ email: guestEmail, is_admin: false, role: "guest", name: "Guest" });
  };

  const handleGuestToAssistant = () => {
    handleGuest();
    // After onLogin the router shows the landing page — jump straight to the chat.
    setTimeout(() => navigate("/assistant"), 0);
  };

  const backToOptions = () => {
    setMode("choose"); setError(""); setNotice(""); setCustInfo(null);
    setPassword(""); setPassword2(""); setToken(""); setCustStep("email");
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguagePicker />
      </div>
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-black tracking-tighter font-['Chivo'] text-slate-900">
            {branding.title || t("brand.name", "Compressors R Us")}
          </h1>
          <p className="text-sm text-slate-500 mt-2">{t("brand.tagline", "Compressor Product Database")}</p>
        </div>

        {mode === "choose" && (
          <div className="space-y-3">
            {error && (
              <div data-testid="login-oauth-error" className="text-[13px] bg-red-50 border border-red-200 text-red-800 rounded-sm px-3 py-2">
                {error}
              </div>
            )}
            {/* AI Assistant call-out — low-friction way for prospects to sanity-check sizing before creating an account */}
            <button data-testid="login-try-ai-button" onClick={handleGuestToAssistant}
              className="group relative w-full overflow-hidden rounded-sm border border-blue-700 bg-gradient-to-r from-blue-700 to-blue-900 text-white px-4 py-3.5 text-left transition-all hover:shadow-lg hover:border-blue-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-white/15 flex items-center justify-center shrink-0 backdrop-blur-sm">
                  <ChatCircleDots size={22} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] font-black uppercase tracking-[0.18em] bg-amber-300 text-slate-900 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1">
                      <Sparkle size={9} weight="fill" /> {t("landing.new", "New")}
                    </span>
                    <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-blue-100/80">{t("landing.no_signup", "No sign-up")}</span>
                  </div>
                  <div className="text-sm font-bold mt-0.5 leading-tight">{t("landing.try_ai_assistant", "Try the AI Assistant")}</div>
                  <div className="text-[11px] text-blue-100/90 mt-0.5 leading-snug">{t("landing.try_ai_subline", "Size a compressor, dryer, or filter in one chat.")}</div>
                </div>
                <ArrowRight size={16} weight="bold" className="text-white/70 group-hover:translate-x-0.5 group-hover:text-white transition-transform shrink-0" />
              </div>
            </button>

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{t("auth.or_signin", "Or sign in")}</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <button data-testid="login-google-button" onClick={handleGoogleLogin}
              disabled={!providers.google}
              title={providers.google ? "" : "Google login is not configured on this server"}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm font-semibold text-slate-700 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed disabled:border-slate-100">
              <GoogleLogo size={20} weight="bold" className="text-slate-600" />
              {t("auth.signin_google", "Continue with Google")}
              {!providers.google && (
                <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  Unavailable
                </span>
              )}
            </button>

            <button
              data-testid="login-microsoft-button"
              onClick={() => handleOAuthLogin("microsoft")}
              disabled={!providers.microsoft}
              title={providers.microsoft ? "" : "Microsoft login is not configured on this server"}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm font-semibold text-slate-700 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed disabled:border-slate-100"
            >
              <MicrosoftOutlookLogo size={20} weight="bold" className="text-slate-600" />
              {t("auth.signin_microsoft", "Continue with Microsoft")}
              {!providers.microsoft && (
                <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  Unavailable
                </span>
              )}
            </button>

            <button
              data-testid="login-apple-button"
              onClick={() => handleOAuthLogin("apple")}
              disabled={!providers.apple}
              title={providers.apple ? "" : "Apple Sign-In is not configured on this server"}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm font-semibold text-slate-700 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed disabled:border-slate-100"
            >
              <AppleLogo size={20} weight="bold" className="text-slate-600" />
              {t("auth.signin_apple", "Continue with Apple")}
              {!providers.apple && (
                <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  Unavailable
                </span>
              )}
            </button>

            <button data-testid="login-email-button" onClick={() => setMode("email")}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm font-semibold text-slate-700">
              <Envelope size={20} weight="bold" className="text-slate-600" />
              {t("auth.signin_email", "Continue with Email")}
            </button>

            <button data-testid="login-guest-button" onClick={handleGuest}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 transition-colors text-sm font-medium text-slate-500">
              <UserCircle size={20} weight="bold" className="text-slate-400" />
              {t("auth.signin_guest", "Continue as Guest")}
            </button>

            {/* iter121 — install-app CTA. Renders only when the
                browser supports PWA install (Android Chrome / Edge /
                modern desktop) OR on iOS Safari (where it opens the
                Add-to-Home-Screen instruction modal). Already-installed
                or unsupported browsers see nothing. */}
            <div className="pt-2">
              <InstallAppButton />
            </div>
          </div>
        )}

        {mode === "email" && (
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{t("login.email_label", "Email Address")}</label>
              <Input data-testid="login-email-input" type="email" required autoFocus placeholder="you@company.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm border-slate-300 h-11" />
              <p className="text-[11px] text-slate-400 mt-1">{t("login.email_hint", "If you're listed as a contact for a US Air customer, we'll walk you through setting up a password.")}</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
              <input
                data-testid="login-remember-checkbox"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 accent-blue-700 rounded-sm"
              />
              <span><b>{t("login.remember_30d", "Remember me for 30 days")}</b> <span className="text-slate-400">{t("login.remember_30d_suffix", "— otherwise signs you out after 7 days.")}</span></span>
            </label>
            {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full rounded-sm bg-blue-700 text-white hover:bg-blue-800 font-semibold h-11">
              {loading ? "Checking..." : "Continue"}
              <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>
            <button type="button" onClick={backToOptions} className="w-full text-sm text-slate-500 hover:text-slate-700 py-1">
              {t("login.back_to_options", "Back to options")}
            </button>
          </form>
        )}

        {mode === "customer" && custInfo && (
          <div className="space-y-4" data-testid="customer-login-flow">
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-start gap-2">
              <Buildings size={18} weight="fill" className="text-blue-700 mt-0.5 shrink-0" />
              <div className="text-xs">
                <div className="font-bold text-blue-900">{custInfo.name}</div>
                <div className="text-blue-700">{custInfo.company_name}</div>
                <div className="text-slate-500 mt-0.5">{custInfo.email}</div>
              </div>
            </div>

            {notice && (
              <div className="bg-green-50 border border-green-200 text-green-700 text-xs px-3 py-2 rounded-sm flex items-start gap-2">
                <CheckCircle size={14} weight="bold" className="shrink-0 mt-0.5" /> {notice}
              </div>
            )}
            {error && <p data-testid="customer-login-error" className="text-sm text-red-600">{error}</p>}

            {custStep === "setPassword" && (
              <form onSubmit={handleSendVerification} className="space-y-3" data-testid="customer-set-password">
                <p className="text-xs text-slate-600">{t("login.create_password_hint", "Create a password for your account. We'll email you a one-time code to verify this email belongs to you.")}</p>
                <Input type="password" required minLength={8} placeholder={t("login.new_password_placeholder", "New password (8+ chars)")}
                  data-testid="customer-password-1" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm border-slate-300 h-10" />
                <Input type="password" required minLength={8} placeholder={t("login.confirm_password_placeholder", "Confirm password")}
                  data-testid="customer-password-2" value={password2} onChange={(e) => setPassword2(e.target.value)}
                  className="rounded-sm border-slate-300 h-10" />
                <Button type="submit" disabled={loading} data-testid="customer-send-verification"
                  className="w-full rounded-sm bg-blue-700 text-white hover:bg-blue-800 font-semibold h-11">
                  {loading ? "Sending..." : "Send Verification Email"}
                </Button>
              </form>
            )}

            {custStep === "verify" && (
              <form onSubmit={handleVerify} className="space-y-3" data-testid="customer-verify">
                <p className="text-xs text-slate-600">{t("login.enter_code_prefix", "Enter the one-time code we emailed to")} <b>{custInfo.email}</b>. {t("login.code_expires", "The code expires in 1 hour.")}</p>
                <Input required placeholder={t("login.verification_code_placeholder", "Verification code")}
                  data-testid="customer-token-input" value={token} onChange={(e) => setToken(e.target.value)}
                  className="rounded-sm border-slate-300 h-10 font-mono" />
                <Button type="submit" disabled={loading} data-testid="customer-verify-submit"
                  className="w-full rounded-sm bg-blue-700 text-white hover:bg-blue-800 font-semibold h-11">
                  {loading ? "Verifying..." : "Verify & Continue"}
                </Button>
                <button type="button" onClick={() => setCustStep("setPassword")}
                  className="w-full text-xs text-slate-500 hover:text-slate-700">{t("login.change_password_resend", "Change password or resend")}</button>
              </form>
            )}

            {custStep === "login" && (
              <form onSubmit={handleCustomerLogin} className="space-y-3" data-testid="customer-login">
                <p className="text-xs text-slate-600 flex items-center gap-1"><LockKey size={13} weight="bold" /> Enter your password to sign in.</p>
                <Input type="password" required placeholder="Password"
                  data-testid="customer-login-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm border-slate-300 h-10" />
                <Button type="submit" disabled={loading} data-testid="customer-login-submit"
                  className="w-full rounded-sm bg-blue-700 text-white hover:bg-blue-800 font-semibold h-11">
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
                <button type="button" onClick={() => setCustStep("setPassword")}
                  className="w-full text-xs text-slate-500 hover:text-slate-700">{t("login.forgot_password", "Forgot password? Reset it")}</button>
              </form>
            )}

            <button type="button" onClick={backToOptions}
              className="w-full text-sm text-slate-500 hover:text-slate-700 py-1">
              {t("login.use_different_email", "Use a different email")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
